import type { RouteObject } from 'react-router-dom';
import type { BlockedArea, ErrorMessage, ScreenMessageVariant } from '@api/assistant/assistantApi';

/**
 * What the assistant's page context is built from: the inline messages and the failed API calls the
 * user has seen recently. Recorded when they happen, not when the question is sent — by the time the
 * user types, the toast has usually closed or they have navigated away to check something.
 *
 * Deliberately outside React: recording an error or registering a message must never re-render
 * anything. Memory only — a reload empties it — and it records nothing until AssistantProvider turns it
 * on for a user who has the assistant.
 *
 * Messages and errors share the lifecycle (start, repeat, end, expire); only the rules differ:
 * - a message is keyed by what it says (key + params + code), so a remount of the same message never
 *   shows up twice. When its last instance unmounts it is kept, off screen, for 10 minutes: it may still
 *   be the problem the user is asking about.
 * - an error is keyed by HTTP method + path. A later success of the same call removes it — the user has
 *   worked around it. The path keeps reservation A's failure when reservation B saves fine; it is used
 *   only for matching here and is never sent.
 */

export const MESSAGE_TTL_MS = 10 * 60 * 1000;
export const ERROR_TTL_MS = 30 * 60 * 1000;
/** Bounds the store itself; the snapshot applies the contract's tighter caps. */
const MAX_STORED = 50;

export interface MessageData {
  key: string;
  params: string[];
  code: string | null;
  variant: ScreenMessageVariant;
}

export interface MessageEntry {
  data: MessageData;
  /** Mounted instances showing this message; on screen while non-empty. */
  instances: Set<string>;
  startedAt: number;
  endedAt: number | null;
}

export interface ErrorData {
  method: string;
  http_method: string;
  status: number | null;
  error_type: string | null;
  error_code: string | null;
  trc_uuid: string | null;
  messages: ErrorMessage[];
}

export interface ErrorEntry {
  data: ErrorData;
  firstAt: number;
  lastAt: number;
  count: number;
}

let enabled = false;
const messages = new Map<string, MessageEntry>();
const instanceSignatures = new Map<string, string>();
const errors = new Map<string, ErrorEntry>();
let routeTree: RouteObject[] | null = null;
/** The blocked areas on screen: page content, and menu entries apart. */
export interface BlockedAreaLists {
  blocked: BlockedArea[];
  navigation: BlockedArea[];
}

let blockedAreasSource: (() => BlockedAreaLists) | null = null;

const signatureOf = (data: MessageData) => JSON.stringify([data.key, data.params, data.code]);

/** Drops the oldest entries once a map grows past MAX_STORED, so a misbehaving page cannot grow it without bound. */
const trim = <T>(map: Map<string, T>, age: (entry: T) => number) => {
  if (map.size <= MAX_STORED) return;
  const oldestFirst = Array.from(map.entries()).sort(([, a], [, b]) => age(a) - age(b));
  oldestFirst.slice(0, map.size - MAX_STORED).forEach(([key]) => map.delete(key));
};

/** Turned on for users who have the assistant; everyone else records nothing. Turning it off also clears. */
export const setPageContextEnabled = (value: boolean) => {
  enabled = value;
  if (!value) clearPageContext();
};

export const isPageContextEnabled = () => enabled;

export const startMessage = (instanceId: string, data: MessageData, now = Date.now()) => {
  if (!enabled) return;
  const signature = signatureOf(data);
  // The same instance now showing something else: the previous message has ended.
  if (instanceSignatures.has(instanceId) && instanceSignatures.get(instanceId) !== signature) endMessage(instanceId, now);
  instanceSignatures.set(instanceId, signature);

  const existing = messages.get(signature);
  if (existing) {
    existing.instances.add(instanceId);
    existing.endedAt = null;
    return;
  }
  messages.set(signature, { data, instances: new Set([instanceId]), startedAt: now, endedAt: null });
  trim(messages, entry => entry.endedAt ?? Number.MAX_SAFE_INTEGER);
};

export const endMessage = (instanceId: string, now = Date.now()) => {
  const signature = instanceSignatures.get(instanceId);
  if (signature === undefined) return;
  instanceSignatures.delete(instanceId);
  const entry = messages.get(signature);
  if (!entry) return;
  entry.instances.delete(instanceId);
  if (entry.instances.size === 0) entry.endedAt = now;
};

/** A failed call: starts an entry, or updates it and counts the repeat while keeping when it first failed. */
export const recordError = (key: string, data: ErrorData, now = Date.now()) => {
  if (!enabled) return;
  const existing = errors.get(key);
  errors.set(key, existing ? { data, firstAt: existing.firstAt, lastAt: now, count: existing.count + 1 } : { data, firstAt: now, lastAt: now, count: 1 });
  trim(errors, entry => entry.lastAt);
};

/** A successful call: whatever failed on the same method + path is no longer a problem. */
export const resolveError = (key: string) => {
  errors.delete(key);
};

export const pruneExpired = (now = Date.now()) => {
  messages.forEach((entry, signature) => {
    if (entry.endedAt !== null && now - entry.endedAt >= MESSAGE_TTL_MS) messages.delete(signature);
  });
  errors.forEach((entry, key) => {
    if (now - entry.lastAt >= ERROR_TTL_MS) errors.delete(key);
  });
};

/** Logout, or a switch of user or company: nothing recorded for the previous one may be sent. */
export const clearPageContext = () => {
  messages.clear();
  instanceSignatures.clear();
  errors.clear();
};

export const getMessageEntries = (): MessageEntry[] => Array.from(messages.values());
export const getErrorEntries = (): ErrorEntry[] => Array.from(errors.values());

/** Set by routes.tsx once the router exists; importing routes.tsx from here would be circular. */
export const setRouteTree = (routes: RouteObject[]) => {
  routeTree = routes;
};
export const getRouteTree = () => routeTree;

/** Set by AssistantProvider, which owns access to the security registry. */
export const setBlockedAreasSource = (source: (() => BlockedAreaLists) | null) => {
  blockedAreasSource = source;
};
export const getBlockedAreas = (): BlockedAreaLists => blockedAreasSource?.() ?? { blocked: [], navigation: [] };
